import React, { useState } from "react";
import { useQuery } from "react-query";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  TextField,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getLastSeenReport } from "../../services/Dashboard";
import { getLastSeenColumns } from "./LastSeenColumns";
import { isValidDate } from "../../services/Utils";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import GpsFixedOutlinedIcon from "@mui/icons-material/GpsFixedOutlined";
import getDataGridToolbarProps from "../common/CustomToolbar";

const theme = {
  pageBg: "#f3f0f9",
  cardBg: "#ffffff",
  border: "#e0daf0",
  borderHover: "#a89cd4",
  accent: "#6c5ce7",
  accentLight: "#f0edfd",
  accentMid: "#ece8fb",
  labelRed: "#c0415a",
  inputBg: "#faf9fd",
  tableHeaderBg: "#faf9fd",
  tableRowHover: "#faf8ff",
  tableRowBorder: "#f0ecf9",
  textPrimary: "#1a1a2e",
  textSecondary: "#6b6b80",
  textMuted: "#a0a0b0",
  radius: "12px",
  radiusSm: "8px",
  radiusXs: "6px",
};

const cardSx = {
  background: theme.cardBg,
  border: `0.5px solid ${theme.border}`,
  borderRadius: theme.radius,
  boxShadow: "none",
};

const toolbarBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  border: `0.5px solid ${theme.border}`,
  borderRadius: theme.radiusXs,
  color: theme.textSecondary,
  fontSize: "12px",
  px: 1.2,
  py: 0.6,
  minWidth: "auto",
  textTransform: "none",
  "&:hover": {
    background: theme.accentLight,
    borderColor: theme.borderHover,
    color: theme.accent,
  },
};

const LastSeenReport = () => {
  const CustomToolbar = getDataGridToolbarProps("Last Seen Report", true);
  const columns = getLastSeenColumns();
  const [rows, setRows] = useState([]);
  const [fetchData, setFetchData] = useState(true);

  const from = new Date(
    new Date(new Date().setMonth(new Date().getMonth() - 1)).setHours(0, 0, 0, 0)
  );
  const to = new Date(new Date().setHours(23, 59, 59, 59));

  const [dateRange, setDateRange] = useState({ from, to });
  const [fromDateInputErrorText, setFromDateInputErrorText] = useState(null);
  const [toDateInputErrorText, setToDateInputErrorText] = useState(null);
  const [searchObj, setSearchObj] = useState({ ...dateRange });

  const { isLoading, isFetching } = useQuery(
    ["lastSeenReport", searchObj],
    getLastSeenReport,
    {
      enabled: fetchData,
      onSuccess: (data) => setRows(data),
      onSettled: () => setFetchData(false),
      refetchOnWindowFocus: false,
    }
  );

  const handleDateChange = (newValue = null, type) => {
    const newDates = dateRange;
    const newFrom = newValue ? new Date(newValue.setHours(0, 0, 0, 0)) : null;
    const newTo = newValue ? new Date(newValue.setHours(23, 59, 59, 59)) : null;

    if (newValue && type === "from") {
      if (moment(newValue).isBefore("2001-01-01")) {
        setFromDateInputErrorText("Error");
        setDateRange({ from: newFrom, to: newDates.to });
        return;
      }
      if (moment(newValue).isAfter(newDates.to)) {
        setFromDateInputErrorText("Date greater than To date!");
        setDateRange({ from: newFrom, to: newDates.to });
        return;
      }
      if (!isValidDate(new Date(newValue))) {
        setFromDateInputErrorText("Invalid date!");
        setDateRange({ from: newFrom, to: newDates.to });
        return;
      }
      setDateRange({ from: newFrom, to: newDates.to });
      setFromDateInputErrorText(null);
    } else if (newValue && type === "to") {
      if (moment(newValue).isBefore(newDates.from)) {
        setToDateInputErrorText("Date less than From date!");
        setDateRange({ from: newDates.from, to: newTo });
        return;
      }
      if (!isValidDate(new Date(newValue))) {
        setToDateInputErrorText("Invalid date!");
        setDateRange({ from: newDates.from, to: newTo });
        return;
      }
      setDateRange({ from: newDates.from, to: newTo });
      setToDateInputErrorText(null);
    } else if (!newValue && type === "to") {
      setDateRange({ from: newDates.from, to: newTo });
    } else if (!newValue && type === "from") {
      setDateRange({ from: newFrom, to: newDates.to });
    }
  };

  const processSearch = () => {
    const searchData = JSON.parse(JSON.stringify(dateRange));
    setSearchObj({
      from: new Date(searchData?.from),
      to: new Date(searchData?.to),
    });
    setFetchData(true);
  };

  const datePickerSlotProps = (label, errorText) => ({
    textField: {
      size: "small",
      variant: "outlined",
      label,
      error: !!errorText,
      helperText: errorText,
      sx: {
        minWidth: 148,
        "& .MuiOutlinedInput-root": {
          borderRadius: theme.radiusSm,
          background: theme.inputBg,
          fontSize: "13px",
          "& fieldset": { borderColor: theme.border, borderWidth: "1px" },
          "&:hover fieldset": { borderColor: theme.borderHover },
          "&.Mui-focused fieldset": {
            borderColor: theme.accent,
            borderWidth: "1.5px",
          },
        },
        "& .MuiInputLabel-root": {
          fontSize: "13px",
          color: theme.labelRed,
          "&.Mui-focused": { color: theme.accent },
        },
        "& .MuiFormHelperText-root": { fontSize: "11px" },
      },
    },
  });

  return (
    <Box
      className="fragmentContainer"
      sx={{ p: 3, background: theme.pageBg, minHeight: "100vh" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <GpsFixedOutlinedIcon sx={{ color: theme.accent, fontSize: 22 }} />
          <Typography
            sx={{ fontSize: "18px", fontWeight: 500, color: theme.textPrimary }}
          >
            Last seen report
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "12px", color: theme.textMuted }}>
          Updated just now
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ ...cardSx, mb: 2 }}>
        <CardContent
          sx={{
            py: "14px !important",
            px: "20px !important",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: theme.textSecondary,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              mt: "10px",
              whiteSpace: "nowrap",
            }}
          >
            Date range
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <FormControl variant="outlined">
                <DatePicker
                  disableFuture
                  label="From"
                  value={dateRange.from}
                  onChange={(val) => handleDateChange(val, "from")}
                  maxDate={new Date()}
                  slotProps={datePickerSlotProps("From", fromDateInputErrorText)}
                />
              </FormControl>

              <Typography sx={{ color: theme.textMuted, fontSize: "18px", mt: "8px" }}>
                →
              </Typography>

              <FormControl variant="outlined">
                <DatePicker
                  disableFuture
                  label="To"
                  value={dateRange.to}
                  onChange={(val) => handleDateChange(val, "to")}
                  minDate={dateRange.from}
                  slotProps={datePickerSlotProps("To", toDateInputErrorText)}
                />
              </FormControl>
            </Box>
          </LocalizationProvider>

          <Box
            sx={{
              width: "1px",
              height: "36px",
              background: theme.border,
              mt: "4px",
              mx: 0.5,
            }}
          />

          <Button
            disabled={!!fromDateInputErrorText || !!toDateInputErrorText}
            variant="outlined"
            startIcon={<SearchIcon sx={{ fontSize: "16px !important" }} />}
            onClick={processSearch}
            sx={{
              borderColor: theme.accent,
              color: theme.accent,
              borderRadius: theme.radiusSm,
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 500,
              px: 2,
              py: "7px",
              mt: "2px",
              "&:hover": {
                background: theme.accentLight,
                borderColor: theme.accent,
              },
              "&.Mui-disabled": {
                borderColor: theme.border,
                color: theme.textMuted,
              },
            }}
          >
            Search
          </Button>

          <Typography
            sx={{
              ml: "auto",
              fontSize: "12px",
              color: theme.textMuted,
              mt: "10px",
              whiteSpace: "nowrap",
            }}
          >
            Showing data for the selected range
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ ...cardSx }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: `0.5px solid ${theme.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 500, color: theme.textPrimary }}
            >
              Vehicles
            </Typography>
            <Chip
              label={rows?.length ?? 0}
              size="small"
              sx={{
                height: "20px",
                fontSize: "11px",
                fontWeight: 600,
                background: theme.accent,
                color: "#fff",
                borderRadius: "20px",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Columns">
              <Button
                size="small"
                startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: "15px !important" }} />}
                sx={toolbarBtnSx}
              >
                Columns
              </Button>
            </Tooltip>
            <Tooltip title="Filter">
              <Button
                size="small"
                startIcon={<FilterListOutlinedIcon sx={{ fontSize: "15px !important" }} />}
                sx={toolbarBtnSx}
              >
                Filter
              </Button>
            </Tooltip>
            <Tooltip title="Export">
              <Button
                size="small"
                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: "15px !important" }} />}
                sx={toolbarBtnSx}
              >
                Export
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ height: "calc(100vh - 310px)", width: "100%" }}>
          <DataGrid
            loading={isLoading || isFetching}
            getRowId={(row) => row?.serialNo + row?.vehicleKey + row?.lastSeen}
            rows={rows || []}
            columns={columns}
            checkboxSelection
            disableSelectionOnClick
            experimentalFeatures={{ newEditingApi: true }}
            components={{ Toolbar: CustomToolbar }}
            sx={{
              border: "none",
              fontFamily: "inherit",
              fontSize: "13px",

              "& .MuiDataGrid-columnHeaders": {
                background: theme.tableHeaderBg,
                borderBottom: `0.5px solid ${theme.border}`,
                borderRadius: 0,
                minHeight: "42px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "12px",
                fontWeight: 600,
                color: theme.textSecondary,
                letterSpacing: "0.03em",
              },
              "& .MuiDataGrid-sortIcon": {
                color: theme.accent,
                opacity: 0.6,
              },

              "& .MuiDataGrid-row": {
                borderBottom: `0.5px solid ${theme.tableRowBorder}`,
                "&:hover": { background: theme.tableRowHover },
                "&.Mui-selected": {
                  background: theme.accentMid,
                  "&:hover": { background: theme.accentLight },
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
                color: theme.textPrimary,
                "&:focus, &:focus-within": { outline: "none" },
              },

              "& .MuiCheckbox-root": {
                color: theme.border,
                "&.Mui-checked": { color: theme.accent },
              },

              "& .MuiDataGrid-footerContainer": {
                borderTop: `0.5px solid ${theme.border}`,
                minHeight: "46px",
              },
              "& .MuiTablePagination-root": {
                fontSize: "12px",
                color: theme.textSecondary,
              },
              "& .MuiTablePagination-select": {
                fontSize: "12px",
              },
              "& .MuiIconButton-root": {
                color: theme.textSecondary,
                "&:hover": {
                  background: theme.accentLight,
                  color: theme.accent,
                },
                "&.Mui-disabled": { color: theme.border },
              },

              "& .MuiDataGrid-overlay": {
                background: "transparent",
              },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default LastSeenReport;